import { useState } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { LayoutDashboard, ChevronsUpDown, LogOut, HardHat, UserCircle2, Briefcase, Bell, Menu, Home } from "lucide-react";
import { LiveIndicator, LiveNotificationsBell } from "@/components/LiveIndicator";
import ThemeToggle from "@/components/ThemeToggle";

const NAV = [
  { to: "/vendor/home", label: "Home", icon: Home },
  { to: "/vendor/tickets", label: "Ticket Board", icon: LayoutDashboard },
  { to: "/vendor/rfp", label: "RFPs — Sealed Bids", icon: Briefcase },
  { to: "/vendor/my-compliance", label: "My Compliance", icon: Briefcase },
  { to: "/vendor/notifications", label: "Notifications", icon: Bell },
  { to: "/vendor/profile", label: "Company Profile", icon: UserCircle2 },
];

export default function VendorShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const closeNav = () => setNavOpen(false);

  const renderNavContent = (onNav) => (
    <>
      <div className="p-5 border-b border-white/10">
        <div className="flex items-center gap-2 mb-5">
          <div className="h-9 w-9 rounded-md bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center">
            <HardHat className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="text-[13px] leading-tight font-semibold font-display">Prestigious</div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Vendor Portal</div>
          </div>
        </div>
        <div className="rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2.5">
          <div className="overline">Company</div>
          <div className="text-sm font-semibold mt-0.5 truncate">{user?.name}</div>
          <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
        </div>
      </div>
      <div className="overflow-y-auto flex-1 pb-6 scroll-slim">
        <nav className="flex flex-col gap-0.5 px-2 mt-6">
          {NAV.map((item) => {
            const Icon = item.icon;
            const isActive = pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={onNav}
                data-testid={`vnav-${item.label.toLowerCase().replaceAll(" ", "-")}`}
                className={
                  "group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors " +
                  (isActive ? "bg-white/[0.06] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]" : "text-zinc-300 hover:bg-white/5 hover:text-white")
                }
              >
                <Icon className={"h-4 w-4 " + (isActive ? "text-emerald-400" : "text-zinc-500 group-hover:text-zinc-300")} />
                <span className="truncate">{item.label}</span>
              </Link>
            );
          })}
        </nav>
      </div>
      <div className="p-3 border-t border-white/10">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="w-full flex items-center gap-3 rounded-md p-2 hover:bg-white/5 transition-colors" data-testid="user-menu-trigger">
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.avatar || ""} alt={user?.name || ""} />
                <AvatarFallback className="bg-emerald-500/20 text-emerald-300">
                  {(user?.name || "V").slice(0,1)}
                </AvatarFallback>
              </Avatar>
              <div className="text-left min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{user?.email}</div>
                <div className="text-[11px] uppercase tracking-widest text-zinc-500">vendor</div>
              </div>
              <ChevronsUpDown className="h-4 w-4 text-zinc-500" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" className="w-56">
            <DropdownMenuLabel>{user?.name}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => { logout(); navigate("/login"); }} data-testid="menu-logout">
              <LogOut className="h-4 w-4 mr-2" />Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );

  return (
    <div className="dark min-h-screen bg-[#050505] text-white grid-lines-dark">
      <div className="flex min-h-screen">
        {/* Desktop sidebar — hidden below lg. */}
        <aside className="hidden lg:flex w-72 border-r border-white/10 bg-[#0a0a0b] shrink-0 flex-col" data-testid="vendor-sidebar">
          {renderNavContent(undefined)}
        </aside>

        <main className="flex-1 min-w-0 flex flex-col">
          <header className="h-14 flex items-center justify-between border-b border-white/10 px-4 sm:px-6 bg-[#0a0a0b]/70 backdrop-blur-md sticky top-0 z-30 gap-2">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => setNavOpen(true)}
                className="lg:hidden -ml-1 p-2 rounded-md hover:bg-white/5 shrink-0"
                aria-label="Open navigation"
                data-testid="vendor-mobile-nav-open"
              >
                <Menu className="h-5 w-5 text-zinc-300" />
              </button>
              <div className="text-sm text-zinc-400 truncate">
                Vendor Portal · <span className="text-white font-medium">{user?.name}</span>
              </div>
              <LiveIndicator />
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <ThemeToggle />
              <LiveNotificationsBell onClick={() => navigate("/vendor/notifications")} />
            </div>
          </header>
          <AnimatePresence mode="wait">
            <motion.div key={pathname} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.25 }} className="p-4 sm:p-6 lg:p-8">
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      <Sheet open={navOpen} onOpenChange={setNavOpen}>
        <SheetContent side="left" className="w-72 max-w-[85vw] p-0 flex flex-col bg-[#0a0a0b] border-white/10 text-white" data-testid="vendor-mobile-nav-sheet">
          <SheetHeader className="sr-only">
            <SheetTitle>Vendor navigation</SheetTitle>
            <SheetDescription>Navigate between ticket board, RFPs, notifications, and profile.</SheetDescription>
          </SheetHeader>
          {renderNavContent(closeNav)}
        </SheetContent>
      </Sheet>
    </div>
  );
}
